// src/utils/openaiService.js
const OPENAI_API_KEY = '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';
const OPENAI_API_URL = 'https://api.openai.com/v1/chat/completions';

// 상품 검색을 위한 자연어 처리
export const searchProductsWithAI = async (searchQuery, products) => {
  try {
    // 상품 데이터를 문자열로 변환 (API 제한 고려)
    const productSummaries = products.slice(0, 50).map(product => ({
      id: product.id,
      name: product.name,
      description: product.description || '',
      category: product.category || '',
      tags: product.tags || [],
      price: product.moqPrices?.[0]?.price || 0
    }));

    const prompt = `
당신은 전자상거래 상품 검색 전문가입니다. 
사용자의 자연어 검색 쿼리를 분석하여 가장 관련성 높은 상품들을 찾아주세요.

사용자 검색어: "${searchQuery}"

사용 가능한 상품들:
${JSON.stringify(productSummaries, null, 2)}

다음 형식으로 JSON 응답해주세요:
{
  "matchedProducts": [
    {
      "id": "상품ID",
      "relevanceScore": 0.95,
      "reason": "매칭 이유"
    }
  ],
  "searchIntent": "사용자의 검색 의도 분석",
  "suggestions": ["관련 검색어 제안1", "관련 검색어 제안2"]
}

관련성 점수는 0-1 사이로, 1에 가까울수록 더 관련성이 높습니다.
`;

    const response = await fetch(OPENAI_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${OPENAI_API_KEY}`
      },
      body: JSON.stringify({
        model: 'gpt-3.5-turbo',
        messages: [
          {
            role: 'system',
            content: '당신은 전자상거래 상품 검색 전문가입니다. 정확하고 관련성 높은 상품을 찾아주세요.'
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        max_tokens: 1000,
        temperature: 0.3
      })
    });

    if (!response.ok) {
      throw new Error(`OpenAI API error: ${response.status}`);
    }

    const data = await response.json();
    const aiResponse = JSON.parse(data.choices[0].message.content);
    
    return {
      matchedProducts: aiResponse.matchedProducts || [],
      searchIntent: aiResponse.searchIntent || '',
      suggestions: aiResponse.suggestions || []
    };
  } catch (error) {
    console.error('AI search error:', error);
    // AI 검색 실패 시 기본 검색으로 폴백
    return fallbackSearch(searchQuery, products);
  }
};

// 상품 추천을 위한 AI 서비스
export const getProductRecommendations = async (currentProduct, allProducts, userPreferences = {}) => {
  try {
    const productSummaries = allProducts
      .filter(p => p.id !== currentProduct.id)
      .slice(0, 30)
      .map(product => ({
        id: product.id,
        name: product.name,
        description: product.description || '',
        category: product.category || '',
        tags: product.tags || [],
        price: product.moqPrices?.[0]?.price || 0,
        rating: product.avgRating || 0
      }));

    const prompt = `
당신은 전자상거래 상품 추천 전문가입니다.
현재 상품을 기반으로 고객에게 최적화된 상품을 추천해주세요.

현재 상품:
- 이름: ${currentProduct.name}
- 설명: ${currentProduct.description || ''}
- 카테고리: ${currentProduct.category || ''}
- 태그: ${currentProduct.tags?.join(', ') || ''}
- 가격: ${currentProduct.moqPrices?.[0]?.price || 0}

추천할 수 있는 상품들:
${JSON.stringify(productSummaries, null, 2)}

다음 형식으로 JSON 응답해주세요:
{
  "recommendations": [
    {
      "id": "상품ID",
      "relevanceScore": 0.92,
      "reason": "추천 이유",
      "type": "similar|complementary|trending"
    }
  ],
  "recommendationStrategy": "추천 전략 설명"
}

추천 타입:
- similar: 유사한 상품
- complementary: 보완 상품 (함께 사용하면 좋은)
- trending: 인기 상품
`;

    const response = await fetch(OPENAI_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${OPENAI_API_KEY}`
      },
      body: JSON.stringify({
        model: 'gpt-3.5-turbo',
        messages: [
          {
            role: 'system',
            content: '당신은 전자상거래 상품 추천 전문가입니다. 고객에게 최적화된 상품을 추천해주세요.'
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        max_tokens: 1000,
        temperature: 0.4
      })
    });

    if (!response.ok) {
      throw new Error(`OpenAI API error: ${response.status}`);
    }

    const data = await response.json();
    const aiResponse = JSON.parse(data.choices[0].message.content);
    
    return {
      recommendations: aiResponse.recommendations || [],
      recommendationStrategy: aiResponse.recommendationStrategy || ''
    };
  } catch (error) {
    console.error('AI recommendation error:', error);
    // AI 추천 실패 시 기본 추천으로 폴백
    return fallbackRecommendations(currentProduct, allProducts);
  }
};

// AI 검색 실패 시 폴백 검색
const fallbackSearch = (searchQuery, products) => {
  const query = searchQuery.toLowerCase();
  const matchedProducts = products
    .filter(product => 
      product.name.toLowerCase().includes(query) ||
      product.description?.toLowerCase().includes(query) ||
      product.category?.toLowerCase().includes(query) ||
      product.tags?.some(tag => tag.toLowerCase().includes(query))
    )
    .slice(0, 10)
    .map(product => ({
      id: product.id,
      relevanceScore: 0.8,
      reason: '키워드 매칭'
    }));

  return {
    matchedProducts,
    searchIntent: '키워드 검색',
    suggestions: []
  };
};

// AI 추천 실패 시 폴백 추천
const fallbackRecommendations = (currentProduct, allProducts) => {
  const recommendations = allProducts
    .filter(p => p.id !== currentProduct.id)
    .filter(p => p.category === currentProduct.category)
    .slice(0, 6)
    .map(product => ({
      id: product.id,
      relevanceScore: 0.7,
      reason: '같은 카테고리',
      type: 'similar'
    }));

  return {
    recommendations,
    recommendationStrategy: '카테고리 기반 추천'
  };
};

// 검색 쿼리 개선을 위한 AI 서비스
export const improveSearchQuery = async (originalQuery) => {
  try {
    const prompt = `
사용자의 검색 쿼리를 개선하여 더 정확한 상품 검색 결과를 제공하세요.

원본 검색어: "${originalQuery}"

다음 형식으로 JSON 응답해주세요:
{
  "improvedQuery": "개선된 검색어",
  "keywords": ["키워드1", "키워드2", "키워드3"],
  "searchType": "exact|broad|semantic"
}
`;

    const response = await fetch(OPENAI_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${OPENAI_API_KEY}`
      },
      body: JSON.stringify({
        model: 'gpt-3.5-turbo',
        messages: [
          {
            role: 'system',
            content: '당신은 검색 쿼리 최적화 전문가입니다. 사용자의 의도를 정확히 파악하여 검색어를 개선해주세요.'
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        max_tokens: 300,
        temperature: 0.3
      })
    });

    if (!response.ok) {
      throw new Error(`OpenAI API error: ${response.status}`);
    }

    const data = await response.json();
    return JSON.parse(data.choices[0].message.content);
  } catch (error) {
    console.error('Query improvement error:', error);
    return {
      improvedQuery: originalQuery,
      keywords: [originalQuery],
      searchType: 'broad'
    };
  }
};
